import { Icon } from '@iconify/react/dist/iconify.js';
import { X } from '@mui/icons-material';
import { Autocomplete, Box, Chip, FormControl, FormHelperText, FormLabel, MenuItem, TextField } from '@mui/material';
import * as React from 'react';


export default function CustomAutocomplete({ values, onChange, size, error, data, title, required, disabled, labelName, valueName, placeholder, includeInputInList, freeSolo, style, multiple = false, inputProps, textFieldPadding }) {

    const toIdStr = (v) => (v == null ? '' : String(v));

    const selectedSingle = React.useMemo(() => {
        const raw = values;
        if (multiple) return null;
        if (raw && typeof raw === 'object') return raw;
        const idStr = toIdStr(raw);
        if (!idStr) return null;
        return (data || []).find((opt) => toIdStr(opt?.[valueName]) === idStr) || null;
    }, [values, multiple, data, valueName]);

    const selectedMultiple = React.useMemo(() => {
        if (!multiple) return [];
        const raw = values;
        if (!raw) return [];
        if (Array.isArray(raw) && typeof raw[0] === 'object') return raw;
        const ids = Array.isArray(raw) ? raw.map(toIdStr) : [toIdStr(raw)];
        const idSet = new Set(ids);
        return (data || []).filter((opt) => idSet.has(toIdStr(opt?.[valueName])));
    }, [values, multiple, data, valueName]);

    const isOptionEqualToValue = (opt, val) =>
        toIdStr(opt?.[valueName]) === toIdStr(val?.[valueName]);

    const getOptionLabel = (opt) => {
        if (typeof opt === 'string') return opt;
        if (!opt || typeof opt !== 'object') return '';
        return opt?.[labelName] ?? '';
    };

    const commonProps = {
        size: size ? size : 'small',
        required,
        disabled,
        options: data || [],
        includeInputInList,
        freeSolo,
        getOptionLabel,
        isOptionEqualToValue,
        popupIcon: (
            <Icon style={{ color: '#434343', fontSize: 12 }} icon="fluent:chevron-down-12-filled" />
        ),
        renderInput: (params) => (
            <TextField
                {...params}
                placeholder={placeholder}
                variant="outlined"
                error={!!error}
                helperText={error || ''}
                InputProps={{
                    ...inputProps,
                    disableUnderline: true,
                }}
                sx={{
                    ...(style || {}),
                    color:
                        multiple
                            ? (!values || (Array.isArray(values) && values.length === 0) ? '#BDBDBD' : 'inherit')
                            : (values === '' || values === undefined || values === null ? '#BDBDBD' : 'inherit'),
                    width: '100%',
                    '& .MuiInput-root:before': { borderColor: '#e6e6e6' },
                    '& .MuiInput-root:after': { borderColor: 'var(--bs-theme-color)' },
                }}
            />
        ),
        sx: style,
    };

    return (
        <>
            <FormControl variant="outlined" error={error ? true : false} fullWidth>
                {title && <FormLabel style={{ fontFamily: 'SophiaPro', fontSize: 14, color: "#594A4E", marginBottom: 3 }}>{title}</FormLabel>}
                {
                    multiple ?
                        <Autocomplete
                            {...commonProps}
                            multiple
                            value={selectedMultiple}
                            filterSelectedOptions={true}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={toIdStr(option?.[valueName])} style={{ backgroundColor: values === option?.[valueName] ? '#d3820036' : 'white', color: values === option?.[valueName] ? '#554000' : '#101010' }}>
                                    {option?.[labelName]}
                                    <div className="d-flex justify-content-between w-100">
                                        <span>{option?.name}</span>
                                        <span className="text-muted">{option?.email}</span>
                                    </div>
                                </Box>
                            )}
                            onChange={(e, arr) => { onChange(e, arr) }}
                            renderInput={(params) =>
                                <TextField {...params} placeholder={placeholder}
                                    variant="outlined"
                                    InputProps={{
                                        ...inputProps,
                                        disableUnderline: true,
                                    }}
                                    error={error ? true : false}
                                    sx={{
                                        style,
                                        color: values === "" || values === undefined || values === null ? "#dbd9d9ff" : "inherit",
                                        width: "100%",
                                        borderRadius: "12px",
                                         "& .MuiOutlinedInput-root": {
                                            borderRadius: "12px",
                                            '& fieldset': {
                                                borderColor: "#dbd9d9ff",
                                            },

                                        },
                                         '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                                            padding: textFieldPadding ? textFieldPadding : '9px 10px',
                                        }
                                    }}
                                />
                            }
                            sx={style}>
                        </Autocomplete> :
                        <Autocomplete
                            {...commonProps}
                            value={selectedSingle}
                            getOptionLabel={(option, value) => option?.[labelName] || values}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={`${option?.[valueName]}`} style={{ backgroundColor: values === option?.[valueName] ? '#FFEBEC' : 'white', color: values === option?.[valueName] ? 'var(--bs-theme-color)' : '#101010' }}>
                                    {option?.[labelName]}
                                </Box>
                            )}
                            isOptionEqualToValue={(option, value) => option?.[valueName] === value?.[valueName]} // Match by valueName
                            placeholder={placeholder}
                            onChange={(e, value) => onChange(e, value)}
                            popupIcon={<Icon style={{ color: '#bdbdbdff', fontSize: "12px" }} icon={"fluent:chevron-down-12-filled"} />}
                            renderInput={(params) =>
                                <TextField {...params} placeholder={placeholder}
                                    variant="outlined"
                                    error={error ? true : false}
                                    sx={{
                                        style,
                                        color: values === "" || values === undefined || values === null ? "#dbd9d9ff" : "inherit",
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "12px",
                                            '& fieldset': {
                                                borderColor: "#dbd9d9ff",
                                            },

                                        },
                                        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                                            padding: textFieldPadding ? textFieldPadding : '9px 10px',
                                        }
                                    }
                                    }
                                />
                            }
                            sx={style}>
                        </Autocomplete>
                }
            </FormControl >
            {error && <FormHelperText sx={{ color: "#BA2727" }}><Icon icon={'fluent:error-circle-24-filled'} height={16} width={16} />{error || ""}</FormHelperText>}


        </>


    );
}
